var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AddAttachmentsBrushaway, AddCategoryBrushaway, Button, ConfirmDialog, PhotoCarousel, Table, TableSize } from '@ui';
import { SectionHeader } from '@ui/molecules/Header/SectionHeader';
import { translate } from '@utils/i18n';
import { createAttachmentCategory } from './services/buildingAttachmentsApi';
import { AttachmentsEmptyState } from './components/AttachmentsEmptyState';
import { AttachmentsFilterBar } from './components/AttachmentsFilterBar';
import { AttachmentsToolbar } from './components/AttachmentsToolbar';
import { useBuildingAttachmentsQuery } from './hooks/useBuildingAttachmentsQuery';
import { useAttachmentPreview } from './hooks/useAttachmentPreview';
import { useAttachmentMassActions } from './hooks/useAttachmentMassActions';
import { useAddAttachmentsUpload } from './hooks/useAddAttachmentsUpload';
import { useUploadProgressToast } from './hooks/useUploadProgressToast';
import { filterAttachmentRows } from './utils/attachmentFiltering';
import { buildCategoryEditOptions, buildCategoryOptions, buildFileTypeOptions, buildVisibilityOptions } from './utils/filterOptions';
import { toAttachmentRow } from './utils/toAttachmentRow';
import { buildAttachmentColumns, SORTABLE_COLUMNS } from './utils/buildAttachmentColumns';
import { buildAddAttachmentsColumns, buildAddAttachmentsLabels } from './utils/addAttachmentsConfig';
import './AttachmentsTableStyles.scss';
const NS = 'features.building_attachments.table';
const VISIBILITY_EDIT_NS = 'features.building_attachments.edit_modal.visibility';
const MASS_ACTIONS_NS = 'features.building_attachments.mass_actions';
const COMMONS_NS = 'features.commons';
/**
 * Fallback only, used when the server did not supply `maxUploadSizeBytes` (e.g. a story or a test
 * rendering the table directly). The real cap is per-tenant and server-owned — presign rejects
 * anything above it with 422 regardless of what this client believes, so the two must agree or
 * users get a late failure instead of an early one.
 */
const DEFAULT_MAX_UPLOAD_SIZE_BYTES = 150 * 1024 * 1024;
// Client-side pagination: the tab already loads the building's whole attachment list in one
// request, so paging here is presentation only — no refetch, and the filter/sort/selection state
// all keep operating over the full filtered set.
const DEFAULT_PAGE_SIZE = 25;
// Newest first. `uploadedSortValue` is `created_at` as a number, so a freshly added attachment
// lands at the top of the first page without the user touching the sort.
const DEFAULT_SORT_KEY = 'uploadedSortValue';
const DEFAULT_SORT_DIRECTION = 'desc';
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
/**
 * The building attachments listing surface: data fetching, client-side search/filter/sort,
 * selection, preview, and the "Add attachments" upload drawer. Mass-action mutations (task-18)
 * are a separate concern — this component only renders the seams they need (`selectedItems`/
 * `onSelectionChange`/`onSelectAll`). Name/Category/Visible-to are inline-editable (task-17).
 *
 * Owns `useAddAttachmentsUpload` (which in turn owns `useDirectUpload`, task-08) — this is "the
 * Page" in `AddAttachmentsBrushaway`'s own module doc: the organism itself never touches the
 * upload queue directly, so the queue survives the drawer closing. A later composition root
 * (task-19) may hoist this ownership further up; until then this is where it lives.
 * `useUploadProgressToast` (task-22) reacts to that same hoisted state to drive the background
 * progress toast and navigate-away warning — it never touches the queue either.
 */
export function AttachmentsTable({ buildingId, onAddAttachment, categoryOptions: categoryEditOptionsProp, hasCustomerPortal = true, hasShowAllAttachmentsInCustomerPortal = false, maxUploadSizeBytes = DEFAULT_MAX_UPLOAD_SIZE_BYTES, canCreateCategory = false }) {
    const t = useCallback((key, options) => translate(key, Object.assign({ namespace: NS }, options)), []);
    const { data, isLoading, isError } = useBuildingAttachmentsQuery(buildingId);
    const apiRows = useMemo(() => data !== null && data !== void 0 ? data : [], [data]);
    const rows = useMemo(() => apiRows.map((row) => toAttachmentRow(row, t)), [apiRows, t]);
    const upload = useAddAttachmentsUpload(buildingId);
    const { openDrawer } = upload;
    useUploadProgressToast({
        buildingId,
        isOpen: upload.isOpen,
        items: upload.items,
        isUploading: upload.isUploading,
        aggregateProgress: upload.aggregateProgress,
        openDrawer
    });
    const handleAddAttachmentClick = useCallback(() => {
        openDrawer();
        onAddAttachment === null || onAddAttachment === void 0 ? void 0 : onAddAttachment();
    }, [openDrawer, onAddAttachment]);
    const [search, setSearch] = useState('');
    const [categoryValues, setCategoryValues] = useState([]);
    const [visibilityValues, setVisibilityValues] = useState([]);
    const [fileTypeValues, setFileTypeValues] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [sortBy, setSortBy] = useState(DEFAULT_SORT_KEY);
    const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
    const [addCategoryOpen, setAddCategoryOpen] = useState(false);
    const [isSavingCategory, setIsSavingCategory] = useState(false);
    const [categoryError, setCategoryError] = useState(null);
    // Categories created in this session, appended to the server-rendered list so the new one is
    // selectable on other rows too without a page reload.
    const [createdCategories, setCreatedCategories] = useState([]);
    // The requesting cell's own commit callback — see AttachmentCategoryCell's prop doc.
    const applyCreatedCategoryRef = useRef(null);
    const clearSelection = useCallback(() => setSelectedIds([]), []);
    const handleRequestAddCategory = useCallback((apply) => {
        applyCreatedCategoryRef.current = apply;
        setCategoryError(null);
        setAddCategoryOpen(true);
    }, []);
    const handleCreateCategory = useCallback((values) => __awaiter(this, void 0, void 0, function* () {
        setIsSavingCategory(true);
        setCategoryError(null);
        try {
            // Captured BEFORE the request, not after it. Two things can null this ref while the request
            // is in flight: the drawer's header close button (Cancel is disabled while saving, the X is
            // not), and `setAddCategoryOpen(false)` below — React 17 does not batch updates made from a
            // Promise callback, so that setState re-renders synchronously and fires `onClose`. Either
            // one used to drop the auto-select on the floor after a successful create.
            const applyToRow = applyCreatedCategoryRef.current;
            const created = yield createAttachmentCategory(values);
            applyCreatedCategoryRef.current = null;
            setCreatedCategories((prev) => [...prev, { id: created.id, label: created.label }]);
            setAddCategoryOpen(false);
            applyToRow === null || applyToRow === void 0 ? void 0 : applyToRow(created.id);
        }
        catch (_a) {
            setCategoryError(translate('category_create_error', { namespace: NS }));
        }
        finally {
            setIsSavingCategory(false);
        }
    }), []);
    const handleCloseAddCategory = useCallback(() => {
        setAddCategoryOpen(false);
        applyCreatedCategoryRef.current = null;
    }, []);
    // E2E-25: changing any filter or the search clears the selection.
    const handleSearchChange = useCallback((value) => {
        setSearch(value);
        clearSelection();
    }, [clearSelection]);
    const handleCategoryChange = useCallback((values) => {
        setCategoryValues(values);
        clearSelection();
    }, [clearSelection]);
    const handleVisibilityChange = useCallback((values) => {
        setVisibilityValues(values);
        clearSelection();
    }, [clearSelection]);
    const handleFileTypeChange = useCallback((values) => {
        setFileTypeValues(values);
        clearSelection();
    }, [clearSelection]);
    const filteredRows = useMemo(() => filterAttachmentRows(rows, search, categoryValues, visibilityValues, fileTypeValues), [rows, search, categoryValues, visibilityValues, fileTypeValues]);
    // Sorting is driven here rather than left to `Table`'s internal state, because `Table` starts
    // unsorted and offers no way to seed a default. Supplying `onSort` puts it in delegated mode, so
    // the header indicator always matches the order actually applied.
    const sortedRows = useMemo(() => {
        if (!sortBy || !sortDirection)
            return filteredRows;
        const factor = sortDirection === 'asc' ? 1 : -1;
        return [...filteredRows].sort((a, b) => {
            const aVal = a[sortBy];
            const bVal = b[sortBy];
            if (aVal == null && bVal == null)
                return 0;
            if (aVal == null)
                return factor;
            if (bVal == null)
                return -factor;
            if (typeof aVal === 'number' && typeof bVal === 'number')
                return (aVal - bVal) * factor;
            return String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' }) * factor;
        });
    }, [filteredRows, sortBy, sortDirection]);
    const handleSort = useCallback((dataKey, direction) => {
        setSortBy(direction ? dataKey : null);
        setSortDirection(direction);
    }, []);
    const categoryOptions = useMemo(() => buildCategoryOptions(rows), [rows]);
    const fileTypeOptions = useMemo(() => buildFileTypeOptions(rows), [rows]);
    const visibilityOptions = useMemo(() => buildVisibilityOptions(t), [t]);
    const handleSelectionChange = useCallback((id, checked) => {
        setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((existing) => existing !== id)));
    }, []);
    const handleSelectAll = useCallback((selectAll) => {
        setSelectedIds(selectAll ? filteredRows.map((row) => row.id) : []);
    }, [filteredRows]);
    const paginationLabels = useMemo(() => ({
        page: t('pagination.page'),
        of: t('pagination.of'),
        itemsPerPage: t('pagination.items_per_page'),
        pageInputAriaLabel: t('pagination.page'),
        pageSizeAriaLabel: t('pagination.page_size'),
        firstPage: t('pagination.first_page'),
        previousPage: t('pagination.previous_page'),
        nextPage: t('pagination.next_page'),
        lastPage: t('pagination.last_page')
    }), [t]);
    const preview = useAttachmentPreview(sortedRows);
    const resolvedCategoryOptions = useMemo(() => [...(categoryEditOptionsProp !== null && categoryEditOptionsProp !== void 0 ? categoryEditOptionsProp : buildCategoryEditOptions(rows, t)), ...createdCategories], [categoryEditOptionsProp, rows, t, createdCategories]);
    const showPortalVisibilityOption = hasCustomerPortal && !hasShowAllAttachmentsInCustomerPortal;
    const uploadLabels = useMemo(() => buildAddAttachmentsLabels(maxUploadSizeBytes), [maxUploadSizeBytes]);
    const uploadColumns = useMemo(() => buildAddAttachmentsColumns(resolvedCategoryOptions, showPortalVisibilityOption, upload.onCategoryChange, upload.onVisibilityChange, 
    // Same add-category drawer the main table's category cell opens — the staged row supplies
    // its own apply callback, so the created category lands on the file that asked for it.
    canCreateCategory ? { label: t('add_category_action'), onRequest: handleRequestAddCategory } : undefined), [
        resolvedCategoryOptions,
        showPortalVisibilityOption,
        upload.onCategoryChange,
        upload.onVisibilityChange,
        canCreateCategory,
        t,
        handleRequestAddCategory
    ]);
    const massActions = useAttachmentMassActions(buildingId, selectedIds, clearSelection);
    // Indeterminate checkbox state for the toolbar's "Update visibility" popover (E2E-31): mixed
    // `showOnCustomerPortal`/`isPrivate` values across the selected rows.
    const selectedPortalValues = useMemo(() => filteredRows.filter((row) => selectedIds.includes(row.id)).map((row) => row.showOnCustomerPortal), [filteredRows, selectedIds]);
    const visibilityChecked = selectedPortalValues.length > 0 && selectedPortalValues.every(Boolean);
    const visibilityIndeterminate = selectedPortalValues.length > 0 && !visibilityChecked && selectedPortalValues.some(Boolean);
    const selectedPrivateValues = useMemo(() => filteredRows.filter((row) => selectedIds.includes(row.id)).map((row) => row.isPrivate), [filteredRows, selectedIds]);
    const techChecked = selectedPrivateValues.length > 0 && selectedPrivateValues.every(Boolean);
    const techIndeterminate = selectedPrivateValues.length > 0 && !techChecked && selectedPrivateValues.some(Boolean);
    const handleRequestRowDelete = useCallback((row) => massActions.requestDelete([row.id], row.name), [massActions.requestDelete]);
    const columns = useMemo(() => buildAttachmentColumns({
        translateFn: t,
        onPreviewClick: preview.handlePreviewClick,
        buildingId,
        categoryOptions: resolvedCategoryOptions,
        addCategoryLabel: canCreateCategory ? t('add_category_action') : undefined,
        onRequestAddCategory: canCreateCategory ? handleRequestAddCategory : undefined,
        showPortalVisibilityOption,
        onRequestRowDelete: handleRequestRowDelete
    }), [
        t,
        preview.handlePreviewClick,
        buildingId,
        resolvedCategoryOptions,
        canCreateCategory,
        handleRequestAddCategory,
        showPortalVisibilityOption,
        handleRequestRowDelete
    ]);
    const { pendingDelete } = massActions;
    // Falls back to a non-empty title even while the dialog is closed (`pendingDelete === null`) —
    // `ConfirmDialog` always renders the underlying `Modal`, which warns without an `ariaLabel`.
    let deleteDialogTitle = translate('delete_confirm.bulk_title', { namespace: MASS_ACTIONS_NS, count: 0 });
    if (pendingDelete === null || pendingDelete === void 0 ? void 0 : pendingDelete.fileName) {
        deleteDialogTitle = translate('delete_confirm.single_title', {
            namespace: MASS_ACTIONS_NS,
            fileName: pendingDelete.fileName
        });
    }
    else if (pendingDelete) {
        deleteDialogTitle = translate('delete_confirm.bulk_title', {
            namespace: MASS_ACTIONS_NS,
            count: pendingDelete.ids.length
        });
    }
    const isLoadingOrError = isLoading || isError;
    const isEmpty = !isLoadingOrError && rows.length === 0;
    const addButton = (React.createElement(Button, { variant: "primary", onClick: handleAddAttachmentClick },
        React.createElement("i", { className: "fa-light fa-plus", "aria-hidden": "true" }),
        " ",
        t('add_button')));
    return (React.createElement("div", { className: "building-attachments-table" },
        React.createElement(SectionHeader, { title: translate('title', { namespace: NS, count: filteredRows.length }), actions: isEmpty ? undefined : addButton }),
        isEmpty ? (React.createElement(AttachmentsEmptyState, { title: t('empty.title'), description: t('empty.description'), ctaLabel: t('empty.cta'), onAdd: handleAddAttachmentClick })) : (React.createElement(React.Fragment, null,
            React.createElement(AttachmentsFilterBar, { search: search, onSearchChange: handleSearchChange, searchPlaceholder: t('search_placeholder'), categoryLabel: t('filters.category_label'), categoryOptions: categoryOptions, categoryValues: categoryValues, onCategoryChange: handleCategoryChange, visibilityLabel: t('filters.visibility_label'), visibilityOptions: visibilityOptions, visibilityValues: visibilityValues, onVisibilityChange: handleVisibilityChange, fileTypeLabel: t('filters.file_type_label'), fileTypeOptions: fileTypeOptions, fileTypeValues: fileTypeValues, onFileTypeChange: handleFileTypeChange }),
            React.createElement(AttachmentsToolbar, { selectedCount: selectedIds.length, selectedLabel: t('toolbar.selected', { count: selectedIds.length }), selectAllLabel: t('toolbar.select_all', { count: filteredRows.length }), onSelectAll: () => setSelectedIds(filteredRows.map((row) => row.id)), clearSelectionLabel: t('toolbar.clear_selection'), onClearSelection: clearSelection, updateCategoryLabel: t('toolbar.update_category'), updateVisibilityLabel: t('toolbar.update_visibility'), deleteLabel: t('toolbar.delete'), categoryOptions: resolvedCategoryOptions, showVisibilityAction: showPortalVisibilityOption, techCheckboxLabel: translate('tech_checkbox_label', { namespace: MASS_ACTIONS_NS }), techChecked: techChecked, techIndeterminate: techIndeterminate, visibilityCheckboxLabel: translate('portal_label', { namespace: VISIBILITY_EDIT_NS }), visibilityChecked: visibilityChecked, visibilityIndeterminate: visibilityIndeterminate, isMutating: massActions.isMutating, onUpdateCategory: massActions.updateCategory, onUpdateTechVisibility: massActions.updateTechVisibility, onUpdateVisibility: massActions.updateVisibility, onRequestDelete: () => massActions.requestDelete(selectedIds) }),
            React.createElement(Table, { className: "building-attachments-table__table", variant: "detail", size: TableSize.Full, columns: columns, data: sortedRows, getItemId: (row) => row.id, selectable: true, selectedItems: selectedIds, onSelectionChange: handleSelectionChange, onSelectAll: handleSelectAll, sortable: true, sortableColumns: SORTABLE_COLUMNS, sortBy: sortBy, sortDirection: sortDirection, onSort: handleSort, paginated: true, pageSize: pageSize, onPageSizeChange: setPageSize, pageSizeOptions: PAGE_SIZE_OPTIONS, paginationLabels: paginationLabels, isLoading: isLoading, isError: isError }))),
        React.createElement(PhotoCarousel, { open: preview.carouselOpen, index: preview.carouselIndex, slides: preview.carouselSlides, onClose: preview.closeCarousel, onIndexChange: preview.setCarouselIndex }),
        React.createElement(PhotoCarousel, { open: preview.pdfOpen, index: 0, slides: preview.pdfUrl ? [{ type: 'document', src: preview.pdfUrl, alt: t('preview.pdf_label') }] : [], onClose: preview.closePdf }),
        React.createElement(ConfirmDialog, { isOpen: pendingDelete !== null, title: deleteDialogTitle, description: translate('delete_confirm.description', { namespace: MASS_ACTIONS_NS }), confirmLabel: translate('delete_confirm.confirm_label', { namespace: MASS_ACTIONS_NS }), confirmVariant: "danger", cancelLabel: translate('cancel', { namespace: COMMONS_NS }), loading: massActions.isMutating, onConfirm: massActions.confirmDelete, onCancel: massActions.cancelDelete }),
        React.createElement(AddAttachmentsBrushaway, { open: upload.isOpen, items: upload.items, isUploading: upload.isUploading, aggregateProgress: upload.aggregateProgress, onFilesSelected: upload.onFilesSelected, onNameChange: upload.onNameChange, onRemoveItem: upload.onRemoveItem, onRetryItem: upload.onRetryItem, onRetryFailed: upload.onRetryFailed, onCancelUpload: upload.onCancelUpload, onSubmit: upload.onSubmit, onClose: upload.onClose, onCloseToBackground: upload.onCloseToBackground, maxSizeBytes: maxUploadSizeBytes, columns: uploadColumns, labels: uploadLabels }),
        React.createElement(AddCategoryBrushaway, { open: addCategoryOpen, isSaving: isSavingCategory, errorMessage: categoryError, onErrorDismiss: () => setCategoryError(null), onSubmit: (values) => {
                void handleCreateCategory(values);
            }, onClose: handleCloseAddCategory, labels: {
                title: t('add_category.title'),
                nameLabel: t('add_category.name_label'),
                namePlaceholder: t('add_category.name_placeholder'),
                descriptionLabel: t('add_category.description_label'),
                descriptionPlaceholder: t('add_category.description_placeholder'),
                submitLabel: t('add_category.submit'),
                cancelLabel: t('add_category.cancel'),
                closeLabel: t('add_category.close')
            } })));
}
